import { type InfiniteData, useQueryClient } from '@tanstack/react-query';

import { useGameSlug } from '@pkg/hooks';
import { arrayShuffle } from '@pkg/utils';

import { type CompetitionLeaderboardResponse } from '@app/client/common/structs';
import { COMPETITIONS_LEADERBOARD_CACHE } from '@app/client/resources/competitions';
import { useSubscribeToTopic, WebsocketTopicIds } from '@app/helpers';
import { type WebsocketMessage } from '@app/store/types/websockets';
import { useFantasyLeaderboardStore } from '@app/store/zustand';

const RANK_CHANGES_TOPIC = 'CompetitionSnapshotRankChange';
export type RankChangeEventType = {
    rank_new: number;
    rank_old: number | null;
    snapshot_date: string;
    competition_wishlist_id: string;
    competition_id: string;
    current_value: number;
    profit_new: number;
};
type EventPayload = {
    competition_started: boolean;
    rank_change_events: RankChangeEventType[];
};

type UseRankChangesProps = {
    slug?: string;
    onRankChanged?: () => void;
};

export const useRankChanges = (props?: UseRankChangesProps) => {
    const { leaderboardKey } = useFantasyLeaderboardStore();
    const { slug: providedSlug, onRankChanged } = props || {};
    const gameSlug = useGameSlug();
    const slug = providedSlug || gameSlug;

    const queryClient = useQueryClient();

    const onmessage = (message) => {
        try {
            const websocketMessage: WebsocketMessage = JSON.parse(message.data);
            const { topic, payload } = websocketMessage;
            if (topic === RANK_CHANGES_TOPIC) {
                const payloadData = payload as unknown as EventPayload;

                queryClient.setQueryData(
                    leaderboardKey || [COMPETITIONS_LEADERBOARD_CACHE, slug],
                    (queryResponse?: InfiniteData<CompetitionLeaderboardResponse | undefined>) => {
                        if (queryResponse) {
                            let rankHasChanged = false;
                            const pages = queryResponse.pages.map((page) => {
                                if (page?.data) {
                                    page.data = page?.data.map((pageData) => {
                                        const rankChange = payloadData.rank_change_events?.find(
                                            (rankChange) =>
                                                rankChange.competition_wishlist_id === pageData.competition_wishlist_id,
                                        );

                                        if (rankChange) {
                                            pageData.rank = rankChange.rank_new;
                                            pageData.current_value = rankChange.current_value;
                                            pageData.profit = rankChange.profit_new;
                                            rankHasChanged = true;
                                        }

                                        return pageData;
                                    });
                                }

                                return page;
                            });

                            if (rankHasChanged && onRankChanged) onRankChanged();

                            return JSON.parse(JSON.stringify({ ...queryResponse, pages }));
                        }

                        return undefined;
                    },
                );
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    };

    useSubscribeToTopic({
        id: WebsocketTopicIds.RankChanges,
        onmessage,
        messageKey: 'RankChanges',
        topic: RANK_CHANGES_TOPIC,
    });
};
export const useTestRankChanges = (props?: UseRankChangesProps) => {
    const { leaderboardKey } = useFantasyLeaderboardStore();
    const { onRankChanged } = props || {};
    const queryClient = useQueryClient();
    const slug = useGameSlug();

    const shuffleRanks = () => {
        queryClient.setQueryData(
            leaderboardKey || [COMPETITIONS_LEADERBOARD_CACHE, slug],
            (queryResponse?: InfiniteData<CompetitionLeaderboardResponse | undefined>) => {
                if (queryResponse?.pages) {
                    const pages = queryResponse.pages.map((page) => {
                        if (page?.data) {
                            page.data = arrayShuffle(page?.data || []).map((pageData, i) => {
                                pageData.rank = i + 1;
                                return { ...pageData };
                            });
                        }

                        return page;
                    });

                    if (onRankChanged) onRankChanged();

                    return JSON.parse(JSON.stringify({ ...queryResponse, pages }));
                }

                return queryResponse;
            },
        );
    };

    return { shuffleRanks };
};
